/**
 * Minimalist Monthly Line Chart
 *
 * Simple SVG-based line chart for displaying monthly climate data.
 */

import type { MonthlyClimateEntry } from '../../types';

const MONTH_LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

interface DataSeries {
  key: keyof MonthlyClimateEntry;
  color: string;
  label: string;
}

interface MonthlyChartProps {
  data: MonthlyClimateEntry[];
  series: DataSeries[];
  height?: number;
  unit?: string;
}

export function MonthlyChart({ data, series, height = 120, unit = '' }: MonthlyChartProps) {
  if (!data || data.length === 0) return null;

  // Sort data by month
  const sortedData = [...data].sort((a, b) => a.month - b.month);

  // Calculate bounds across all series
  let minVal = Infinity;
  let maxVal = -Infinity;
  for (const s of series) {
    for (const d of sortedData) {
      const val = d[s.key] as number;
      if (val < minVal) minVal = val;
      if (val > maxVal) maxVal = val;
    }
  }

  // Add padding to range
  const range = maxVal - minVal || 1;
  const padding = range * 0.1;
  minVal -= padding;
  maxVal += padding;

  const width = 280;
  const chartHeight = height - 24; // Leave room for labels
  const chartTop = 4;

  // Generate path for each series
  const generatePath = (s: DataSeries): string => {
    const points = sortedData.map((d, i) => {
      const x = (i / 11) * (width - 20) + 10;
      const val = d[s.key] as number;
      const y = chartTop + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    });
    return points.join(' ');
  };

  // Format value for tooltip
  const formatVal = (val: number): string => {
    if (Math.abs(val) >= 100) return Math.round(val).toString();
    return val.toFixed(1);
  };

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        style={{ maxHeight: height }}
      >
        {/* Grid lines */}
        <line
          x1="10"
          y1={chartTop}
          x2="10"
          y2={chartTop + chartHeight}
          stroke="currentColor"
          strokeOpacity="0.1"
        />
        <line
          x1="10"
          y1={chartTop + chartHeight}
          x2={width - 10}
          y2={chartTop + chartHeight}
          stroke="currentColor"
          strokeOpacity="0.1"
        />

        {/* Y-axis labels */}
        <text
          x="8"
          y={chartTop + 4}
          fontSize="8"
          fill="currentColor"
          fillOpacity="0.5"
          textAnchor="end"
        >
          {formatVal(maxVal)}{unit}
        </text>
        <text
          x="8"
          y={chartTop + chartHeight}
          fontSize="8"
          fill="currentColor"
          fillOpacity="0.5"
          textAnchor="end"
        >
          {formatVal(minVal)}{unit}
        </text>

        {/* Data lines */}
        {series.map((s) => (
          <path
            key={s.key}
            d={generatePath(s)}
            fill="none"
            stroke={s.color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {/* Data points */}
        {series.map((s) =>
          sortedData.map((d, i) => {
            const x = (i / 11) * (width - 20) + 10;
            const val = d[s.key] as number;
            const y = chartTop + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
            return (
              <circle
                key={`${s.key}-${i}`}
                cx={x}
                cy={y}
                r="2"
                fill={s.color}
              />
            );
          })
        )}

        {/* Month labels */}
        {MONTH_LABELS.map((label, i) => {
          const x = (i / 11) * (width - 20) + 10;
          return (
            <text
              key={i}
              x={x}
              y={height - 4}
              fontSize="8"
              fill="currentColor"
              fillOpacity="0.5"
              textAnchor="middle"
            >
              {label}
            </text>
          );
        })}
      </svg>

      {/* Legend */}
      {series.length > 1 && (
        <div className="mt-1 flex justify-center gap-4 text-xs text-muted-foreground">
          {series.map((s) => (
            <div key={s.key} className="flex items-center gap-1">
              <div
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
